import { apiUrl } from "./apiBase";
import { getAuthToken } from "./auth";
import {
  isAndroidPlayStoreApp,
  isIOSAppStoreApp,
  isNativeStoreApp,
} from "./appMode";

const DEVICE_ID_KEY = "ga_native_push_device_id";
const PUSH_TOKEN_KEY = "ga_native_push_token";
const PUSH_PLATFORM_KEY = "ga_native_push_platform";

function makeDeviceId() {
  try {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch {}

  return `ga-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function getCapacitorPlugin(name) {
  if (!isNativeStoreApp() || typeof window === "undefined") return null;
  return window?.Capacitor?.Plugins?.[name] || null;
}

export function getNativePushPlugin() {
  return getCapacitorPlugin("PushNotifications");
}

export function getFirebaseMessagingPlugin() {
  return (
    getCapacitorPlugin("FirebaseMessaging") ||
    getCapacitorPlugin("FCM") ||
    null
  );
}

export function getNativePlatform() {
  if (isIOSAppStoreApp()) return "ios";
  if (isAndroidPlayStoreApp()) return "android";

  try {
    const platform = window?.Capacitor?.getPlatform?.();
    return platform === "ios" || platform === "android" ? platform : "";
  } catch {
    return "";
  }
}

export function getOrCreatePushDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY) || "";
    if (!id) {
      id = makeDeviceId();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return makeDeviceId();
  }
}

export function getStoredPushToken() {
  try {
    return localStorage.getItem(PUSH_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function storePushToken(token, platform = getNativePlatform()) {
  try {
    if (token) localStorage.setItem(PUSH_TOKEN_KEY, String(token));
    if (platform) localStorage.setItem(PUSH_PLATFORM_KEY, String(platform));
  } catch {}
}

export async function getFirebaseMessagingToken() {
  const plugin = getFirebaseMessagingPlugin();
  if (!plugin?.getToken) return "";

  const result = await plugin.getToken();
  return String(result?.token || result?.value || "").trim();
}

export async function createAndroidPushChannels() {
  if (getNativePlatform() !== "android") return;

  const plugin = getNativePushPlugin();
  if (!plugin?.createChannel) return;

  const channels = [
    {
      id: "ga_live",
      name: "GAStreaming Live Alerts",
      description: "Alerts when a GAStreaming channel goes live.",
      importance: 5,
      visibility: 1,
      vibration: true,
      sound: "ga_live.wav",
    },
    {
      id: "ga_social",
      name: "GAStreaming Mentions & Social",
      description: "Username mentions, replies, and social activity.",
      importance: 4,
      visibility: 1,
      vibration: true,
      sound: "ga_social.wav",
    },
    {
      id: "ga_important",
      name: "GAStreaming Tournaments & Updates",
      description: "Tournament activity and important GAStreaming announcements.",
      importance: 5,
      visibility: 1,
      vibration: true,
      sound: "ga_important.wav",
    },
  ];

  for (const channel of channels) {
    try {
      await plugin.createChannel(channel);
    } catch (error) {
      console.warn("[push] Could not create Android channel", channel.id, error);
    }
  }
}

export async function getNativePushPermission() {
  const pushPlugin = getNativePushPlugin();
  const firebasePlugin = getFirebaseMessagingPlugin();
  const permissionPlugin =
    pushPlugin?.checkPermissions ? pushPlugin : firebasePlugin;

  if (!permissionPlugin?.checkPermissions) return "unavailable";

  try {
    const status = await permissionPlugin.checkPermissions();
    return status?.receive || "prompt";
  } catch {
    return "prompt";
  }
}

export async function requestNativePushRegistration() {
  const platform = getNativePlatform();
  const pushPlugin = getNativePushPlugin();
  const firebasePlugin = getFirebaseMessagingPlugin();
  const permissionPlugin =
    pushPlugin?.requestPermissions ? pushPlugin : firebasePlugin;

  if (!pushPlugin && !firebasePlugin) {
    throw new Error("Push notifications are not installed in this app build.");
  }

  let permission = await getNativePushPermission();

  if (permission === "prompt" || permission === "prompt-with-rationale") {
    if (!permissionPlugin?.requestPermissions) {
      throw new Error("This app build cannot request notification permission.");
    }
    const result = await permissionPlugin.requestPermissions();
    permission = result?.receive || permission;
  }

  if (permission !== "granted") {
    const error = new Error("Notification permission was not granted.");
    error.code = "permission_denied";
    throw error;
  }

  if (platform === "android") {
    await createAndroidPushChannels();

    if (!pushPlugin?.register) {
      throw new Error("Android push registration is unavailable in this app build.");
    }

    await pushPlugin.register();
    return permission;
  }

  if (platform === "ios") {
    if (pushPlugin?.register) {
      await pushPlugin.register();
    }

    if (firebasePlugin?.getToken) {
      try {
        const token = await getFirebaseMessagingToken();
        if (token) await registerPushTokenWithServer(token);
      } catch (error) {
        console.warn("[push] iOS FCM token was not ready immediately", error);
      }
    }

    return permission;
  }

  throw new Error("Unsupported native push platform.");
}

export async function registerPushTokenWithServer(
  token,
  authToken = getAuthToken()
) {
  const cleanToken = String(token || "").trim();
  const platform = getNativePlatform();

  if (!cleanToken || !platform || !authToken) {
    return { ok: false, skipped: true };
  }

  storePushToken(cleanToken, platform);

  const response = await fetch(apiUrl("/push/register"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      token: cleanToken,
      platform,
      deviceId: getOrCreatePushDeviceId(),
      appVersion: platform === "ios" ? "1.0 (3)" : "1.1 (2)",
      userAgent: navigator.userAgent || "",
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      data?.message || data?.error || "Could not register this phone."
    );
  }

  window.dispatchEvent(
    new CustomEvent("ga:push-registration", { detail: data })
  );

  return data;
}

export async function unregisterCurrentPushDevice(
  authToken = getAuthToken()
) {
  if (!authToken || !isNativeStoreApp()) {
    return { ok: false, skipped: true };
  }

  const platform = getNativePlatform();
  const token = getStoredPushToken();

  const response = await fetch(apiUrl("/push/unregister"), {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      token,
      platform,
      deviceId: getOrCreatePushDeviceId(),
    }),
  }).catch(() => null);

  return (
    response?.json?.().catch(() => ({ ok: response?.ok })) || {
      ok: false,
    }
  );
}

export function notificationHref(notificationOrAction) {
  const notification =
    notificationOrAction?.notification || notificationOrAction || {};
  const data = notification?.data || {};

  const value = String(
    data.href ||
      data.path ||
      notification.href ||
      notification.link ||
      "/"
  ).trim();

  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  return value;
}

export function openPushDestination(notificationOrAction) {
  const href = notificationHref(notificationOrAction);
  if (typeof window === "undefined") return;

  const currentLocation =
    `${window.location.pathname}` +
    `${window.location.search}` +
    `${window.location.hash}`;

  if (currentLocation === href) {
    window.dispatchEvent(
      new CustomEvent("ga:push-deep-link", { detail: { href } })
    );
    return;
  }

  window.location.assign(href);
}
